import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import axios from "axios";
import { getAccessToken } from "../utils/common";
import { toastState, loadingState } from "../utils/atom";

const KIND = { 2: "댓글", 3: "대댓글" };
const KIND_BADGE = { 2: "bg-secondary", 3: "bg-info" };

const CommentDetail = ({ modalClose, idx, boardId }) => {
    const [toast, setToast] = useRecoilState(toastState);
    const [loading, setLoading] = useRecoilState(loadingState);
    const [post, setPost] = useState(null); // 레시피(글 역할)
    const [comments, setComments] = useState([]); // 댓글(step=2) + 각 replies(step=3)

    useEffect(() => {
        fetch();
    }, [idx]);

    async function fetch() {
        setLoading(true);
        try {
            const { data } = await axios({
                url: `${process.env.REACT_APP_HOST}/comments/tree/${idx}?board_id=${boardId || ""}`,
                method: "GET",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${getAccessToken()}`,
                },
            });
            if (data && data.code == 1) {
                setPost(data.post ?? null);
                setComments(data.comments ?? []);
            }
        } catch (error) {
            setToast({ isToast: true, message: "댓글 조회 실패" });
        }
        setLoading(false);
    }

    // 삭제 (is_use = 0 숨김처리)
    async function handleDelete(targetIdx) {
        if (!window.confirm("삭제(숨김) 하시겠습니까?")) return;
        setLoading(true);
        try {
            await axios({
                url: `${process.env.REACT_APP_HOST}/comments/delete`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getAccessToken()}`,
                },
                data: { idx: targetIdx },
            });
            setToast({ isToast: true, message: "삭제되었습니다." });
            await fetch();
        } catch (error) {
            setToast({ isToast: true, message: "삭제에 실패했습니다." });
        }
        setLoading(false);
    }

    // 전역 차단/해제 토글
    async function handleBlock(targetIdx, isBlock) {
        const nextBlock = isBlock ? 0 : 1;
        if (!window.confirm(nextBlock ? "차단 하시겠습니까?" : "차단을 해제 하시겠습니까?")) return;
        setLoading(true);
        try {
            await axios({
                url: `${process.env.REACT_APP_HOST}/comments/block`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getAccessToken()}`,
                },
                data: { idx: targetIdx, block: nextBlock },
            });
            setToast({ isToast: true, message: nextBlock ? "차단되었습니다." : "차단 해제되었습니다." });
            await fetch();
        } catch (error) {
            setToast({ isToast: true, message: "차단 처리에 실패했습니다." });
        }
        setLoading(false);
    }

    // 차단/삭제 시 안내 문구로 대체 (차단 우선).
    function maskContent(o) {
        if (o.is_block == 1) return "차단된 댓글입니다.";
        if (o.is_use == 0) return "삭제된 댓글 입니다";
        return o.memo || "(내용없음)";
    }

    // 테이블 한 행. level: 0=댓글(step2), 1=대댓글(step3) → 내용 들여쓰기로 트리 표현
    function renderRow(o, level) {
        const isDeleted = o.is_use == 0;
        const isBlocked = o.is_block == 1;
        const masked = isDeleted || isBlocked;
        return (
            <tr key={o.idx} style={{ backgroundColor: level > 0 ? "#f8f9fa" : undefined }}>
                <td className="text-nowrap">
                    {o.name1 || "(이름없음)"}
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                        {o.id}
                    </div>
                </td>
                <td style={{ paddingLeft: `${12 + level * 28}px`, minWidth: "320px" }}>
                    <div className="d-flex align-items-start" style={{ gap: "6px" }}>
                        {level > 0 && <span className="text-muted">└</span>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <span
                                style={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontStyle: masked ? "italic" : "normal",
                                    color: masked ? "#999" : "inherit",
                                }}
                            >
                                {maskContent(o)}
                            </span>
                            {!masked && o.filename0 && (
                                <div>
                                    <img
                                        src={o.filename0}
                                        alt=""
                                        width={44}
                                        height={44}
                                        style={{ objectFit: "cover" }}
                                        className="rounded border mt-1"
                                    />
                                </div>
                            )}
                            {/* 사용자 신고 내역(신고자: 사유 (시간)). 관리자는 원문과 함께 확인 후 차단/삭제 판단 */}
                            {o.report_cnt > 0 && (
                                <div
                                    className="mt-1 px-2 py-1 border border-warning rounded bg-warning-subtle"
                                    style={{ whiteSpace: "pre-wrap", fontSize: "12px" }}
                                >
                                    <strong>🚨 신고 {o.report_cnt}건</strong>
                                    {o.report_cnt >= 3 && <span className="ms-1">(사용자에게 자동 숨김 중)</span>}
                                    {"\n"}
                                    {o.report_memo}
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td className="text-center text-nowrap text-muted" style={{ fontSize: "12px" }}>
                    {o.created}
                </td>
                <td className="text-center text-nowrap">
                    {isDeleted && <span className="badge bg-dark me-1">삭제됨</span>}
                    {isBlocked && <span className="badge bg-danger">차단됨</span>}
                    {o.report_cnt > 0 && <span className="badge bg-warning text-dark me-1">신고 {o.report_cnt}</span>}
                    {!masked && !(o.report_cnt > 0) && <span className="badge bg-success">정상</span>}
                </td>
                <td className="text-center text-nowrap">
                    <button
                        type="button"
                        className={`btn btn-sm me-1 ${isBlocked ? "btn-outline-danger" : "btn-danger"}`}
                        onClick={() => handleBlock(o.idx, isBlocked)}
                    >
                        {isBlocked ? "차단해제" : "차단"}
                    </button>
                    {!isDeleted && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleDelete(o.idx)}
                        >
                            삭제
                        </button>
                    )}
                </td>
            </tr>
        );
    }

    return (
        <div
            className="position-absolute top-0 start-0 bg-light w-100"
            style={{ zIndex: "999", display: "block", minHeight: "100vh" }}
        >
            <div className="d-flex justify-content-between align-items-center py-3 px-3 mb-0 bg-body border-bottom">
                <h6 className="mb-0">댓글 관리 (레시피 #{idx})</h6>
                <button type="button" className="btn-close" onClick={() => modalClose(false)}></button>
            </div>

            <div className="px-3 py-3">
                {/* 레시피(글 역할) - 읽기전용 헤더 */}
                {post && (
                    <div className="d-flex align-items-center bg-white border rounded p-2 mb-3" style={{ gap: "10px" }}>
                        {post.filename0 && (
                            <img
                                src={post.filename0}
                                alt=""
                                width={48}
                                height={48}
                                style={{ objectFit: "cover" }}
                                className="rounded border"
                            />
                        )}
                        <div>
                            <span className="badge bg-primary me-2">레시피</span>
                            <strong>{post.title || "(삭제된 레시피)"}</strong>
                            <div className="text-muted" style={{ fontSize: "12px" }}>
                                #{post.idx}
                            </div>
                        </div>
                    </div>
                )}

                {/* 댓글(step=2) → 대댓글(step=3) 트리 테이블 */}
                <h6 className="text-muted my-2" style={{ fontSize: "13px" }}>
                    댓글 {comments.length}개
                </h6>
                <div className="table-responsive">
                    <table className="table table-bordered table-sm align-middle mb-0">
                        <thead>
                            <tr style={{ backgroundColor: "#f1f3f5" }}>
                                <th className="text-center text-nowrap" width={140}>
                                    작성자
                                </th>
                                <th className="text-center">내용</th>
                                <th className="text-center text-nowrap" width={90}>
                                    등록일
                                </th>
                                <th className="text-center text-nowrap" width={90}>
                                    상태
                                </th>
                                <th className="text-center text-nowrap" width={130}>
                                    관리
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {comments.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-4">
                                        댓글이 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                comments.map((c) => (
                                    <React.Fragment key={c.idx}>
                                        {renderRow(c, 0)}
                                        {Array.isArray(c.replies) && c.replies.map((r) => renderRow(r, 1))}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CommentDetail;
